import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

/**
 * RFC 7807 problem detail, as the API returns it (`application/problem+json`),
 * plus the two identifiers this backend adds for support.
 */
export interface ProblemDetail {
  type: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  correlationId?: string;
  tenantId?: string;
}

/** Problem types the backend raises for the XTM Cloud connection it proxies. */
export const XTM_CLOUD_PROBLEM_PREFIX = 'urn:problem-type:xtm-cloud.';
/** The stored XTM Cloud credentials were rejected by XTM Cloud itself. */
export const XTM_CLOUD_AUTHENTICATION = 'urn:problem-type:xtm-cloud.authentication';
/** XTM Cloud is unreachable / the circuit breaker is open. Transient. */
export const XTM_CLOUD_UNAVAILABLE = 'urn:problem-type:xtm-cloud.unavailable';

/** The style guide chosen for a file is not one of the organization's any more (422 on a lane decision). */
export const LANE_DECISION_STYLE_GUIDE_UNKNOWN = 'urn:problem-type:lane-decision.style-guide-unknown';

/** The XTM Cloud customer chosen for a file is not active, or is another organization's (422 on a lane decision). */
export const LANE_DECISION_XTM_CUSTOMER_NOT_AVAILABLE = 'urn:problem-type:lane-decision.xtm-customer-not-available';
/** The project template chosen for a file is not one the chosen customer can use (422 on a lane decision). */
export const LANE_DECISION_XTM_TEMPLATE_NOT_AVAILABLE = 'urn:problem-type:lane-decision.xtm-template-not-available';

/** The batch's uploaded files are no longer kept (410 on a lane decision, a retry or an upload commit). */
export const UPLOAD_SOURCE_EXPIRED = 'urn:problem-type:upload.source-expired';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * A parsed JSON body, as opposed to a Blob / FormData / ArrayBuffer.
 *
 * This is not pedantry: `Blob.type` holds the MIME string, so a blob-download
 * endpoint that failed with `application/problem+json` satisfied a bare
 * `typeof data.type === 'string'` check and reported its *content type* as the
 * problem type. Requiring a plain object rules that out.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Reads the problem detail out of an RTK Query error, or null when the response
 * carried none. Only `type` is required — everything else is optional, so a
 * partial body still yields something usable rather than nothing.
 */
export function getProblemDetail(error: unknown): ProblemDetail | null {
  if (!isRecord(error)) return null;
  const data = (error as FetchBaseQueryError & { data?: unknown }).data;
  if (!isPlainObject(data) || typeof data.type !== 'string') return null;
  return data as unknown as ProblemDetail;
}

/**
 * True for any failure the backend attributes to the XTM Cloud connection
 * rather than to this app's own session.
 *
 * This distinction matters in the base query: XTM Cloud rejecting the stored
 * credentials also arrives as HTTP 401, and treating it as an expired session
 * burns a pointless silent renew and then reports "access denied" — telling the
 * user their XTM Go account lacks access, when in fact the tenant's XTM Cloud
 * password needs re-entering.
 */
export function isXtmCloudProblem(error: unknown): boolean {
  return getProblemDetail(error)?.type.startsWith(XTM_CLOUD_PROBLEM_PREFIX) ?? false;
}

/** True when the API refused the request because the batch's uploaded files are no longer kept. */
export function isSourceExpired(error: unknown): boolean {
  if (!isRecord(error) || error.status !== 410) return false;
  return getProblemDetail(error)?.type === UPLOAD_SOURCE_EXPIRED;
}
